import AddIcon from "@mui/icons-material/Add";
import { Container, Grid, Typography } from "@mui/material";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTable from "../../../components/CustomTable";
import CustomTextField from "../../../components/CustomTextField";
import CustomSelect from "../../../components/Select/Select";
import {
  createStockIssue,
  getClinicStocks,
  getStockIssues,
  updateStockIssues,
  updateStockIssueStatusById,
} from "../../../redux/reducers/clinicSlice";
import { getClinicVets } from "../../../redux/reducers/vetSlice";
import { AppColors } from "../../../util/AppColors";
import { addIcon } from "../../../util/CommonIconStyle";
import { requestFilters } from "../../../util/dropList";

const tblHdrs = [
  "sNo",
  "id",
  // "itemName",
  "department",
  "requestedBy",
  "requestedDate",
  "issueDate",
  "status",
  "extraStatus",
];
const tblHdrsOthers = [
  "sNo",
  "id",
  // "itemName",
  "requestedBy",
  "requestedDate",
  "status",
];
const reqTxt = "This field is required";
const initObj = {
  name: null,
  quantity: null,
  nameErr: false,
  qtyErr: false,
  issQtyErr: false,
  issueQuantity: null,
};

const initVal = {
  requestedBy: null,
  requestedDate: new Date(),
  requestedByErr: false,
};

const StockRequest = () => {
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState("");
  const [modValues, setModValues] = useState(initVal);
  const [addedList, setAddedList] = useState([{ ...initObj, i: 0 }]);
  const [modVsble, setModVsble] = useState(false);
  const [canModVsble, setCanModVsble] = useState(false);
  const [issueModVsble, setIssueModVsble] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selFilterTypes, setSelFilterTypes] = useState(["All"]);
  const { stockIssues, clinicStocks } = useSelector((state) => state?.clinic);
  const vets = useSelector((state) => state?.vet?.vets);
  const role = localStorage.getItem("role");

  useEffect(() => {
    onApiCall();
    dispatch(getClinicStocks());
    dispatch(getClinicVets("?type=all"));
  }, []);

  const onApiCall = () => {
    dispatch(getStockIssues());
  };

  const handleCreate = () => {
    setModVsble(true);
  };

  const handleModClose = () => {
    setModVsble(false);
    setAddedList([{ ...initObj, i: 0 }]);
    setModValues(initVal);
  };

  const handleDelete = (si) => {
    setSelectedItem(si);
    setCanModVsble(true);
  };

  const handleIssue = (si) => {
    const reqList = si?.stockIssueDetails?.map((li, i) => ({
      ...li,
      issueQuantity: li?.quantity,
      i,
    }));
    setSelectedItem(si);
    setAddedList(reqList);
    setIssueModVsble(true);
  };

  const getFilteredData = () => {
    if (selFilterTypes?.includes("All")) return stockIssues?.data ?? [];
    return stockIssues?.data?.filter((li) => {
      return selFilterTypes?.some(
        (type) => type?.toLowerCase() === li?.status?.toLowerCase()
      );
    });
  };

  const getStatus = (details) => {
    const statusPriority = ["Pending", "Completed", "Cancelled"];
    for (const status of statusPriority) {
      if (
        details.some((li) => li.status?.toLowerCase() === status?.toLowerCase())
      ) {
        return status;
      }
    }
    return "Pending"; // default if none match
  };

  const isDisabledStatus = (dta) =>
    dta?.some((li) =>
      ["completed", "cancelled"].includes(li?.status?.toLowerCase())
    );

  const getVetById = (id) => {
    return vets?.vets?.find((li) => li?.doctorId === id);
  };

  const getTableHeaders = () => {
    return getFilteredData()?.map((si, i) => ({
      ...si,
      sNo: i + 1,
      id: si?.issueId,
      requestedBy:
        typeof si?.requestedBy === "string"
          ? si?.requestedBy
          : si?.requestedName
          ? si?.requestedName
          : getVetById(si?.requestedBy)?.name ?? "",
      requestedDate: moment(new Date(si?.requestedDate)).format("DD MMM YYYY"),
      department: getVetById(si?.requestedBy)?.department ?? "",
      status: getStatus(si?.stockIssueDetails),
      extraStatus: (
        <div className="flex-row-ali-cen">
          <div>
            <CustomButton
              smallBtn
              text="Cancel"
              redBtn={si?.status === "Pending"}
              onClick={() => handleDelete(si)}
              disabled={isDisabledStatus(si?.stockIssueDetails)}
              grayBtn={isDisabledStatus(si?.stockIssueDetails)}
            />
          </div>
          <div className="ml15">
            <CustomButton
              smallBtn
              text="Issue"
              onClick={() => handleIssue(si)}
              disabled={isDisabledStatus(si?.stockIssueDetails)}
              grayBtn={isDisabledStatus(si?.stockIssueDetails)}
            />
          </div>
        </div>
      ),
    }));
  };

  const handleChange = (name, value) => {
    const reqObj = { ...modValues, [name]: value };
    if (name === "requestedBy") {
      reqObj.requestedByErr = value ? false : true;
    }
    setModValues(reqObj);
  };

  const handleValidate = () => {
    const reqList = addedList?.map((li) => {
      const reqObj = {
        ...li,
        nameErr: li?.name === "" || li?.name?.length === 0 || !li?.name,
        qtyErr:
          li?.quantity === "" ||
          li?.quantity?.length === 0 ||
          !li?.quantity ||
          Number(li?.quantity) < 0,
      };
      return reqObj;
    });
    setAddedList(reqList);
    return reqList?.some((li) => li?.nameErr || li?.qtyErr);
  };

  const handleSave = () => {
    if (role === "ROLE_CLINIC") {
      if (!modValues?.requestedBy?.value) {
        setModValues({ ...modValues, requestedByErr: true });
        return;
      }
    }
    if (handleValidate() || addedList?.length === 0) return;
    const stockIssueDetails = addedList?.map((li) => ({
      itemName: li?.name ?? "",
      quantity: Number(li?.quantity ?? 1),
      status: "Pending",
    }));
    const reqObj = { stockIssueDetails };
    if (role === "ROLE_CLINIC") {
      reqObj.requestedBy = modValues?.requestedBy?.value;
      reqObj.requestedDate = moment(new Date(modValues?.requestedDate)).format(
        "YYYY-MM-DD"
      );
    }
    dispatch(createStockIssue(reqObj)).then((res) => {
      if (res?.payload) {
        dispatch(getStockIssues());
        handleModClose();
      }
    });
  };

  const handleAddNew = () => {
    setAddedList([...addedList, { ...initObj, i: addedList?.length }]);
  };

  const getPresList = () => {
    const reqList = clinicStocks?.data?.filter((dta) => {
      return (
        (dta?.category?.toLowerCase() === "medication" ||
          dta?.category?.toLowerCase() === "diet" ||
          dta?.category?.toLowerCase() === "nutrition and supplements" ||
          dta?.category?.toLowerCase() === "fluid therapy" ||
          dta?.category?.toLowerCase() === "other parasite treatment" ||
          dta?.category?.toLowerCase() === "deworming" ||
          dta?.category?.toLowerCase() === "flea & tick treatment") &&
        dta?.stockBatch?.find((btch) => btch?.billlable === "Y")
      );
    });
    return reqList?.map((li) => ({
      name: li?.tradeName,
      value: li?.tradeName,
      stockId: li?.stockId,
      type: li?.category,
      productName: li?.productName,
    }));
  };

  const onChange = (name, value, i) => {
    const reqList = [...addedList];
    reqList[i] = {
      ...reqList[i],
      [name]: value,
      nameErr:
        name === "name"
          ? value === "" || value?.length === 0 || !value
          : reqList[i]?.nameErr,
      qtyErr:
        name === "quantity"
          ? value === "" || value?.length === 0 || !value || Number(value) < 0
          : reqList[i]?.qtyErr,
      issQtyErr:
        name === "issueQuantity"
          ? value === "" || value?.length === 0 || !value || Number(value) < 1
          : reqList[i]?.issQtyErr,
    };
    setAddedList(reqList);
  };

  const onCloseCanMod = () => {
    setCanModVsble(false);
    setSelectedItem(null);
  };

  const onConfrimCancel = () => {
    if (!selectedItem?.issueId) return;
    const data = selectedItem?.stockIssueDetails?.map((li) => ({
      quantity: li?.quantity,
      status: "Cancelled",
      itemName: li?.itemName,
    }));
    dispatch(
      updateStockIssueStatusById({
        id: selectedItem?.issueId,
        status: "Cancelled",
        data,
      })
    ).then((res) => {
      if (res?.payload) {
        onCloseCanMod();
        onApiCall();
      }
    });
  };

  const onCloseIssueMod = () => {
    setIssueModVsble(false);
    setSelectedItem(null);
    setAddedList([{ ...initObj, i: 0 }]);
  };

  const isIssueQuantiyNotValid = () => {
    const reqList = addedList?.map((li) => {
      const reqObj = {
        ...li,
        issQtyErr:
          li?.issueQuantity === "" ||
          li?.issueQuantity?.length === 0 ||
          !li?.issueQuantity ||
          Number(li?.issueQuantity) < 1,
      };
      return reqObj;
    });
    setAddedList(reqList);
    return addedList?.some((li) => li?.issQtyErr);
  };

  const onUpdateIssue = () => {
    if (isIssueQuantiyNotValid()) return;
    const data = addedList?.map((li) => {
      return {
        id: li?.id,
        issueQuantity: Number(li?.issueQuantity),
        quantity: li?.quantity,
        status: "Completed",
        itemName: li?.itemName,
      };
    });
    dispatch(updateStockIssues({ issueId: selectedItem?.issueId, data })).then(
      (res) => {
        if (res?.payload) {
          onCloseIssueMod();
          onApiCall();
        }
      }
    );
  };

  const handleFilterTypes = (name) => {
    const isPrev = selFilterTypes?.find((con) => con === name);
    if (name === "All") {
      setSelFilterTypes(["All"]);
    } else {
      let reqCons = isPrev
        ? selFilterTypes?.filter((con) => con !== name)
        : [...selFilterTypes, name];
      reqCons = reqCons?.filter((con) => con !== "All");
      if (reqCons?.length === 0) reqCons = ["All"];
      setSelFilterTypes(reqCons);
    }
  };

  const getVetList = () => {
    return vets?.vets?.map((li) => ({ name: li?.name, value: li?.doctorId }));
  };

  return (
    <Container maxWidth="xl">
      <div className="com-mar back-white pv10">
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={12}
          xl={12}
          className="flex-row-center"
        >
          <div className="flex-row">
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              lg={12}
              xl={12}
              style={{ width: "600px" }}
            >
              <CustomTextField
                placeholder="Search"
                fullWidth
                backgroundColor={AppColors.lightPink}
                value={searchValue}
                handleChange={(e) => setSearchValue(e.target.value)}
              />
            </Grid>
          </div>
          <div className="ml20">
            <div>
              <CustomButton
                text="Create Request"
                smallBtn
                onClick={handleCreate}
              />
            </div>
          </div>
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <div className="flex-row-ali-cen mt15 scrollx mh20 mbminus">
            {requestFilters?.map((sl) => (
              <Typography
                className={`gray4 fs14 ph10 pv5 br5 txt-semi-bold cursor mh5 ${
                  selFilterTypes?.includes(sl?.name)
                    ? "green-back3"
                    : "gray-back2"
                }`}
                key={sl?.name}
                onClick={() => handleFilterTypes(sl?.name)}
              >
                {sl?.name}
              </Typography>
            ))}
          </div>
        </Grid>
        <CustomTable
          columns={role === "ROLE_CLINIC" ? tblHdrs : tblHdrsOthers}
          datas={getTableHeaders()}
          grey
          stockIssue
        />
      </div>
      <CustomModal
        open={issueModVsble}
        onClose={onCloseIssueMod}
        header="Stock Requests"
        modalWidth={40}
        modal
      >
        <div className="scroll-60vh">
          <div className="light-blue-back pv10">
            <div className="flex-row-between-align-center">
              <div className="w33Per">
                <div className="flex-center">
                  <div>
                    <Typography className="gray29 fs12 font-medium">
                      Requested By
                    </Typography>
                    <Typography className="black22 fs12 txt-semi-bold">
                      {selectedItem?.requestedBy}
                    </Typography>
                  </div>
                </div>
              </div>
              <div className="w33Per">
                <div className="flex-center">
                  <div>
                    <Typography className="gray29 fs12 font-medium">
                      Request Date
                    </Typography>
                    <Typography className="black22 fs12 txt-semi-bold">
                      {moment(new Date(selectedItem?.requestedDate)).format(
                        "DD MMM YYYY"
                      )}
                    </Typography>
                  </div>
                </div>
              </div>
              <div className="w33Per">
                <div className="flex-center">
                  <div>
                    <Typography className="gray29 fs12 font-medium">
                      Department
                    </Typography>
                    <Typography className="black22 fs12 txt-semi-bold">
                      {selectedItem?.stockIssueDetails?.[0]?.department}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="gry-hor-split2 mt15" />
          <div className="flex-row-ali-cen mt5">
            <div className="w40Per">
              <Typography className="font-bold gray29 fs12 ml5">
                Product Name
              </Typography>
            </div>
            <div className="w30Per">
              <Typography className="font-bold gray29 fs12">
                Requested Quantity
              </Typography>
            </div>
            <div className="w30Per">
              <Typography className="font-bold gray29 fs12">
                Issue Quantity
              </Typography>
            </div>
          </div>
          <div className="gry-hor-split2 mt5" />
          {addedList?.map((al, i) => {
            return (
              <div className="flex-row mt10" key={`iss_${i}}`}>
                <div className="w40Per pr10">
                  <Typography className="ml5 black22 fs14 txt-regular">
                    {al?.itemName}
                  </Typography>
                </div>
                <div className="w30Per">
                  <Typography className="ml5 black22 fs14 txt-regular">
                    {al?.quantity}
                  </Typography>
                </div>
                <div className="w30Per">
                  <CustomTextField
                    value={al?.issueQuantity}
                    error={al?.issQtyErr}
                    helperText={al?.issQtyErr ? reqTxt : ""}
                    handleChange={(e) => {
                      onChange("issueQuantity", e?.target?.value, al?.i);
                    }}
                    type="number"
                    fullWidth
                  />
                </div>
              </div>
            );
          })}
          {addedList?.length > 0 ? (
            <div className="flex1-center mv10">
              <div>
                <CustomButton text="Submit" smallBtn onClick={onUpdateIssue} />
              </div>
            </div>
          ) : null}
        </div>
      </CustomModal>
      <CustomModal
        open={modVsble}
        onClose={handleModClose}
        header="Create"
        modalWidth={40}
        modal
      >
        <div className="scroll-60vh">
          {role === "ROLE_CLINIC" ? (
            <div className="flex-row">
              <div className="w50Per pr10">
                <Typography className="font-bold blue-color fs12 mb5">
                  Requested By
                </Typography>
                <CustomSelect
                  list={getVetList()}
                  value={modValues?.requestedBy}
                  error={modValues?.requestedByErr}
                  helperText={
                    modValues?.requestedByErr ? "This is required field" : ""
                  }
                  select
                  handleChange={(e, value) => {
                    handleChange("requestedBy", value);
                  }}
                  search
                />
              </div>
              <div className="w50Per pl10">
                <Typography className="font-bold blue-color fs12 mb5">
                  Request Date
                </Typography>
                <CustomTextField
                  value={modValues?.requestedDate}
                  handleChange={(e) => {
                    handleChange("requestedDate", e?.target?.value);
                  }}
                  mobileDate
                  fullWidth
                />
              </div>
            </div>
          ) : null}
          <div
            className={`flex-row-ali-cen ph20 ${
              role === "ROLE_CLINIC" ? "mt20" : ""
            }`}
          >
            <div className="w70Per">
              <Typography className="font-bold blue-color fs12">
                Product Name
              </Typography>
            </div>
            <div className="w30Per">
              <Typography className="font-bold blue-color fs12">
                Quantity
              </Typography>
            </div>
          </div>
          <div className="blue-hor-split mt5" />
          {addedList?.map((al, i) => {
            return (
              <div className="flex-row ph20 mt10 plan-bor" key={`iss_${i}}`}>
                <div className="w70Per pr10">
                  <CustomSelect
                    list={getPresList()}
                    value={al?.name}
                    error={al?.nameErr}
                    helperText={al?.nameErr ? reqTxt : ""}
                    addNewSelect
                    mandatoryField
                    handleChange={(e) => {
                      onChange("name", e?.value ?? e, al?.i);
                    }}
                    plusBtn
                  />
                </div>
                <div className="w30Per">
                  <CustomTextField
                    value={al?.quantity}
                    error={al?.qtyErr}
                    helperText={al?.qtyErr ? reqTxt : ""}
                    handleChange={(e) => {
                      onChange("quantity", e?.target?.value, al?.i);
                    }}
                    type="number"
                    fullWidth
                  />
                </div>
              </div>
            );
          })}
          <div className="flex-row-ali-cen mt20 cursor" onClick={handleAddNew}>
            <div className="flex-center plus-icon">
              <AddIcon sx={addIcon} />
            </div>
            <Typography className="blue-color fs16 ml5 font-bold">
              Add
            </Typography>
          </div>
          {addedList?.length > 0 ? (
            <div className="flex1-center mv10">
              <div>
                <CustomButton text="Submit" smallBtn onClick={handleSave} />
              </div>
            </div>
          ) : null}
        </div>
      </CustomModal>
      <CustomModal
        open={canModVsble}
        onClose={onCloseCanMod}
        header="Cancel Request"
        modal
        modalWidth={40}
      >
        <Typography className="txt-regular fs16 ml10">
          Are you sure you want to cancel the item?
        </Typography>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <div className="clinic-mod-btn-pos mb10 mt15">
            <div className="mr10">
              <CustomButton
                text="Yes"
                redBtn
                onClick={onConfrimCancel}
                smallBtn
              />
            </div>
            <div className="ml10">
              <CustomButton text="No" onClick={onCloseCanMod} smallBtn />
            </div>
          </div>
        </Grid>
      </CustomModal>
    </Container>
  );
};

export default StockRequest;
